import React, { useState, useMemo, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import AssessmentPopup from './AssessmentPopup';
import { useAssessmentContext } from '../AssessmentContext';
import { useTests } from '../../../hooks/useTests';
import TestsDataTable from './TestsDataTable';
import CreateTestPanel from './CreateTestPanel';
import BatchWizard from '../Wizard/BatchWizard';
import './assessment.css';
import '../assessment-module-mobile.css';

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'internal', label: 'Internal', dbValue: 'Enova' },
  { value: 'external', label: 'External', dbValue: 'HR Recruitment-Fresher' },
];

const TestDashboard = () => {
  const { setActiveModule } = useAssessmentContext();
  const { tests, loading, refreshTests, updateTest, deleteTest, removeTestPack } = useTests();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleCreate = () => {
    if (isCreateOpen) {
      setIsCreateOpen(false);
      setEditingTest(null);
    } else {
      setEditingTest(null);
      setIsCreateOpen(true);
    }
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setIsCreateOpen(true);
  };

  const handleDeleteTest = (test) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this test?',
      message: 'Are you sure you want to delete this test? This action cannot be undone.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          await deleteTest(test.id);
          setPopupConfig({ isOpen: false });
          toast.success('The assessment has been removed.');
          refreshTests();
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          toast.error('Failed to delete assessment');
        }
      }
    });
  };

  const handleClosePanel = () => {
    setIsCreateOpen(false);
    setEditingTest(null);
  };

  const handleSuccessfulSave = () => {
    handleClosePanel();
    refreshTests();
  };

  const handleInitializeBatch = (assessment) => {
    setSelectedAssessment(assessment);
    setIsWizardOpen(true);
  };

  const activeCategoryFilter = CATEGORY_FILTER_OPTIONS.find((opt) => opt.value === categoryFilter);

  const filteredTests = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return (tests || []).filter((t) => {
      const name = t.name || '';
      const category = t.category || '';
      const categoryLabel =
        category === 'Enova'
          ? 'internal'
          : category === 'HR Recruitment-Fresher'
            ? 'external'
            : category.toLowerCase();
      const matchesSearch =
        !query ||
        name.toLowerCase().includes(query) ||
        category.toLowerCase().includes(query) ||
        categoryLabel.includes(query);
      const matchesCategory =
        !activeCategoryFilter?.dbValue || category === activeCategoryFilter.dbValue;
      return matchesSearch && matchesCategory;
    });
  }, [tests, searchQuery, categoryFilter, activeCategoryFilter]);

  return (
    <div className="assess-mod-page assess-mod-inventory-page p-6 min-h-0 w-full">
      <div className="assess-mod-inventory-header assess-mod-header flex items-center justify-between mb-8">
        <div className="assess-mod-header-brand">
          <h3 className="text-2xl font-bold text-slate-800">Learning Management System</h3>
        </div>
        <div className="assess-mod-inventory-actions assess-mod-actions-row flex gap-3">
          <button 
            id="btn_new_test_toggle"
            className={`btn-assessment btn-assessment-danger ${isCreateOpen ? 'active' : ''}`}
            onClick={handleToggleCreate}
          >
            <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] transition-colors ${isCreateOpen ? 'bg-red-800' : 'bg-white/20'}`}>
              1
            </span>
            <span>Create New Test</span>
          </button>
          <button 
            className="btn-assessment btn-assessment-danger"
            onClick={() => setActiveModule('templates')}
          >
            <span className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full text-[10px]">2</span>
            <span>Template Master</span>
          </button>
        </div>
      </div>

      <CreateTestPanel 
        isOpen={isCreateOpen}
        onClose={handleClosePanel}
        editingTest={editingTest}
        onSuccess={handleSuccessfulSave}
      />

      <BatchWizard 
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        assessment={selectedAssessment}
      />

      <div className="assess-mod-inventory-card assess-mod-card bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 overflow-visible mb-12">
        <div className="assess-mod-inventory-toolbar assess-mod-tabs-row px-8 py-6 border-b border-slate-100 bg-slate-100 flex items-center justify-end">
          <div className="assess-mod-inventory-filters flex items-center gap-4">
            <div className="relative" ref={categoryDropdownRef}>
              <button 
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="flex items-center gap-3 bg-white h-10 px-4 rounded-xl border border-slate-100 shadow-sm hover:border-red-200 transition-all min-w-[160px]"
              >
                <i className="bi bi-filter text-slate-400 text-sm"></i>
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide flex-1 text-left">
                  {activeCategoryFilter?.label || 'All'}
                </span>
                <i className={`bi bi-chevron-down text-xs text-slate-400 transition-transform duration-300 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl z-[100] py-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                  {CATEGORY_FILTER_OPTIONS.map((option) => (
                    <div 
                      key={option.value || 'all'}
                      onClick={() => {
                        setCategoryFilter(option.value);
                        setIsCategoryDropdownOpen(false);
                      }}
                      className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wide cursor-pointer transition-all flex items-center gap-2 ${
                        categoryFilter === option.value 
                          ? 'bg-red-50 text-red-600' 
                          : 'text-slate-500 hover:bg-slate-50 hover:text-red-500'
                      }`}
                    >
                      <i className={`bi ${
                        option.value === 'internal' ? 'bi-building-fill' : 
                        option.value === 'external' ? 'bi-globe2' : 
                        'bi-grid-fill'
                      } text-[10px]`}></i>
                      <span>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Input */}
            <div className="flex items-center gap-3 bg-white h-10 px-4 rounded-xl border border-slate-100 shadow-sm w-[320px] focus-within:border-red-200 transition-all">
              <i className="bi bi-search text-slate-400 text-sm"></i>
              <input 
                type="text" 
                className="text-xs font-bold text-slate-600 border-0 focus:ring-0 p-0 w-full placeholder:text-slate-400 uppercase tracking-wide outline-none bg-transparent"
                placeholder="Search by name or category..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="assess-mod-inventory-table-wrap assess-mod-table-wrap p-0">
          {loading ? (
            <div className="py-32 flex flex-col items-center justify-center gap-6">
              <div className="w-14 h-14 border-[4px] border-slate-100 border-t-red-600 rounded-full animate-spin"></div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Synchronizing Inventory...</p>
            </div>
          ) : (
            <TestsDataTable 
              data={filteredTests}
              onEdit={handleEditTest}
              onDelete={handleDeleteTest}
              refreshData={refreshTests}
              onPackDeleted={removeTestPack}
            />
          )}
        </div>
      </div>
      <AssessmentPopup 
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
    </div>
  );
};

export default TestDashboard;
